// An isolated Auth instance verifies the other login without replacing the
// account the user is currently using. Never persist that temporary session.
const accountGuard = (firebase, user) => {
  let changed = false
  const unsubscribe = firebase.auth().onAuthStateChanged?.(current => {
    if (current?.uid !== user.uid) changed = true
  })
  return {
    check: () => {
      if (changed || firebase.auth().currentUser?.uid !== user.uid) throw new Error('The IntelliKey account changed.')
    },
    close: () => { changed = true; unsubscribe?.() }
  }
}

export const isolatedAccountAuth = async firebase => {
  const app = firebase.initializeApp(firebase.app().options, `account-link-${crypto.randomUUID?.() || Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-')}`)
  const auth = app.auth()
  try {
    await auth.setPersistence(firebase.auth.Auth.Persistence.NONE)
  } catch (error) {
    await app.delete()
    throw error
  }
  let closed = false
  return { auth, close: async () => {
    if (closed) return
    closed = true
    try { await auth.signOut() } finally { await app.delete() }
  } }
}

export const verifyAndMergeProvider = async ({ firebase, provider, credential, linkUser, mergeAccount }) => {
  const guard = accountGuard(firebase, linkUser)
  let session
  try {
    guard.check()
    session = await isolatedAccountAuth(firebase)
    const result = credential ? await session.auth.signInWithCredential(credential) : await session.auth.signInWithPopup(provider)
    guard.check()
    const sourceIdToken = await result.user.getIdToken(true)
    guard.check()
    await mergeAccount(sourceIdToken, linkUser.uid)
    guard.check()
    return { user: linkUser, merged: true }
  } finally { guard.close(); await session?.close() }
}

export const verifyAndMergePhone = async ({ firebase, phoneNumber, appVerifier, mergeAccount }) => {
  const user = firebase.auth().currentUser
  if (!user) throw new Error('Sign in before linking a phone number.')
  const guard = accountGuard(firebase, user)
  let verificationId
  try { verificationId = await new firebase.auth.PhoneAuthProvider(firebase.auth()).verifyPhoneNumber(phoneNumber, appVerifier) }
  catch (error) { guard.close(); throw error }
  return {
      expectedUID: user.uid,
      cancel: async () => { guard.close() },
      confirm: async code => {
        const check = guard.check
        check()
        const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, code)
        try {
          const result = await user.linkWithCredential(credential)
          check()
          guard.close()
          return result
        } catch (error) {
          if (!['auth/credential-already-in-use', 'auth/provider-already-linked'].includes(error.code)) throw error
        }
        check()
        const session = await isolatedAccountAuth(firebase)
        try {
          const result = await session.auth.signInWithCredential(credential)
          check()
          const sourceIdToken = await result.user.getIdToken(true)
          check()
          await mergeAccount(sourceIdToken, user.uid)
          check()
          guard.close()
          return { user, merged: true }
        } finally { await session.close() }
      }
    }
}
